import { Agent, AtpAgent, type AppBskyFeedDefs } from '@atproto/api'
import type { OAuthSession } from '@atproto/oauth-client-browser'
import { type MastodonSession, normalizeAcct } from '@/lib/mastodon'
import { type PixelfedSession, normalizeAcct as pixelfedNormalizeAcct } from '@/lib/pixelfed'
import type { MastodonStatus } from '@/types/mastodon'
import type { PostsPage } from '@/types/posts'
import { mapFeedViewPost } from '@/services/postsService'
import { mapMastodonStatus } from '@/services/mastodonService'
import { mapPixelfedStatus } from '@/services/pixelfedService'
import { SEARCH_RESULTS_LIMIT, POST_SEARCH_PAGE_SIZE } from '@/constants/search'

export interface SearchResult {
    id: string
    display: string
    avatar?: string
    protocol: 'bluesky' | 'mastodon' | 'pixelfed'
    did?: string
    handle?: string
    displayName?: string
    isVerified?: boolean
}

interface MastodonAccount {
    acct: string
    avatar: string
    display_name?: string
}

/**
 * Search for Bluesky users by handle or username.
 * Always uses a session-bound Agent (never the AppView-aware publicAgent):
 * Blacksky's app.bsky.actor.searchActorsTypeahead backend returns a persistent
 * 502 (mackinac#2985), so people search stays pinned to the default Bluesky
 * AppView regardless of the selected AppView. Revisit if Blacksky's search
 * recovers. `_publicAgent` is accepted but ignored so call sites keep a
 * consistent signature with searchBlueskyPosts.
 * @throws {Error} Network errors or API failures
 */
export async function searchBlueskyUsers(
    query: string,
    session: OAuthSession,
    _publicAgent?: AtpAgent
): Promise<SearchResult[]> {
    const agent = new Agent(session)
    const { data } = await agent.searchActorsTypeahead({
        q: query,
        limit: SEARCH_RESULTS_LIMIT,
    })

    if (!data?.actors) {
        throw new Error('Invalid response from Bluesky search')
    }

    return data.actors.map(actor => ({
        id: actor.handle,
        display: actor.handle,
        avatar: actor.avatar,
        protocol: 'bluesky' as const,
        did: actor.did,
        handle: actor.handle,
        displayName: actor.displayName,
        isVerified:
            (actor.verification as { verifiedStatus?: string } | undefined)?.verifiedStatus ===
            'valid',
    }))
}

/**
 * Search for Mastodon users by handle or username.
 * @throws {Error} Network errors or API failures
 */
export async function searchMastodonUsers(
    query: string,
    mastodonSession: MastodonSession
): Promise<SearchResult[]> {
    const res = await fetch(
        `${mastodonSession.instance_url}/api/v2/search?q=${encodeURIComponent(query)}&type=accounts&limit=${SEARCH_RESULTS_LIMIT}`,
        {
            headers: {
                Authorization: `Bearer ${mastodonSession.access_token}`,
            },
        }
    )

    if (!res.ok) {
        throw new Error(`Mastodon search failed: ${res.status}`)
    }

    const data = await res.json()
    return data.accounts.map((account: MastodonAccount) => {
        const fullHandle = normalizeAcct(account.acct, mastodonSession.instance_url)
        return {
            id: fullHandle,
            display: fullHandle,
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty display_name is meaningfully "no display name", same as missing
            displayName: account.display_name || undefined,
            avatar: account.avatar,
            protocol: 'mastodon' as const,
        }
    })
}

/**
 * Search for Bluesky posts by query string.
 * Uses the `searchPosts` lexicon with sort=top.
 * Returns a PostsPage with cursor for pagination.
 * Always uses a session-bound Agent, never the AppView-aware publicAgent:
 * Blacksky's app.bsky.feed.searchPosts backend returns a persistent 502
 * (mackinac#2985), so post search stays pinned to the default Bluesky AppView
 * regardless of the selected AppView. Revisit if Blacksky's search recovers.
 * `_publicAgent` is accepted but ignored to keep a consistent signature with
 * searchBlueskyUsers.
 * @throws {Error} Network errors or API failures
 */
export async function searchBlueskyPosts(
    query: string,
    session: OAuthSession,
    cursor?: string,
    _publicAgent?: AtpAgent
): Promise<PostsPage> {
    // Use Agent(session) which routes through the user PDS (same as actor typeahead).
    // public.api.bsky.app now blocks cross-origin requests to searchPosts, but the
    // PDS proxies AppView calls and has CORS configured for mackinac origins.
    const agent = new Agent(session)
    const { data } = await agent.app.bsky.feed.searchPosts({
        q: query,
        sort: 'top',
        limit: POST_SEARCH_PAGE_SIZE,
        cursor,
    })

    if (!data?.posts) {
        throw new Error('Invalid response from Bluesky post search')
    }

    const posts = data.posts.map((postView: AppBskyFeedDefs.PostView) =>
        mapFeedViewPost({ post: postView })
    )

    return {
        posts,
        cursor: data.cursor,
    }
}

/**
 * Search for Mastodon posts (statuses) by query string.
 * Uses the Mastodon v2 search API with type=statuses.
 * Mastodon v2 search uses offset-based pagination (no Link headers).
 * Returns a PostsPage with mastodonCursor (next offset) for pagination.
 * @throws {Error} Network errors or API failures
 */
export async function searchMastodonPosts(
    query: string,
    mastodonSession: MastodonSession,
    offset?: number
): Promise<PostsPage> {
    const params = new URLSearchParams({
        q: query,
        type: 'statuses',
        limit: String(POST_SEARCH_PAGE_SIZE),
    })
    if (offset !== undefined) params.set('offset', String(offset))

    const res = await fetch(`${mastodonSession.instance_url}/api/v2/search?${params}`, {
        headers: {
            Authorization: `Bearer ${mastodonSession.access_token}`,
        },
    })

    if (!res.ok) {
        throw new Error(`Mastodon post search failed: ${res.status}`)
    }

    const data = await res.json()
    const posts = (data.statuses ?? []).map((status: MastodonStatus) =>
        mapMastodonStatus(status, mastodonSession.instance_url)
    )
    // Offset-based pagination: advance if we got any results, exhausted when API returns 0.
    // We can't compare against POST_SEARCH_PAGE_SIZE because instances may cap lower than our limit.
    const currentOffset = offset ?? 0
    const nextOffset = posts.length === 0 ? undefined : currentOffset + posts.length

    return {
        posts,
        mastodonCursor: nextOffset !== undefined ? String(nextOffset) : undefined,
    }
}

interface MastodonHashtagHistoryEntry {
    uses?: unknown
}

interface MastodonHashtagApiEntry {
    name?: unknown
    history?: unknown
}

export interface MastodonHashtagResult {
    name: string
    /** Sum of valid history uses counts; omitted when no valid values exist. */
    recentUses?: number
}

/** Sum valid nonnegative numeric `history[].uses` values, coercing strings via Number. */
function computeRecentUses(history: unknown): number | undefined {
    if (!Array.isArray(history)) return undefined
    let sum = 0
    let hasValidEntry = false
    for (const entry of history as MastodonHashtagHistoryEntry[]) {
        const raw = entry?.uses
        if (typeof raw !== 'string' && typeof raw !== 'number') continue
        const n = Number(raw)
        if (Number.isNaN(n) || n < 0) continue
        sum += n
        hasValidEntry = true
    }
    return hasValidEntry ? sum : undefined
}

/**
 * Search for Mastodon hashtags by query string.
 * Returns raw tag names — encoding for navigation is owned by the caller.
 * @throws {Error} Network errors, HTTP failures, or a malformed top-level payload
 */
export async function searchMastodonHashtags(
    mastodonSession: MastodonSession,
    query: string,
    limit: number
): Promise<MastodonHashtagResult[]> {
    const res = await fetch(
        `${mastodonSession.instance_url}/api/v2/search?q=${encodeURIComponent(query)}&type=hashtags&limit=${limit}`,
        {
            headers: {
                Authorization: `Bearer ${mastodonSession.access_token}`,
            },
        }
    )

    if (!res.ok) {
        throw new Error(`Mastodon hashtag search failed: ${res.status}`)
    }

    const data = await res.json()
    if (!Array.isArray(data?.hashtags)) {
        throw new Error('Invalid response from Mastodon hashtag search')
    }

    const results: MastodonHashtagResult[] = []
    for (const tag of data.hashtags as MastodonHashtagApiEntry[]) {
        if (typeof tag?.name !== 'string') continue
        results.push({ name: tag.name, recentUses: computeRecentUses(tag.history) })
    }
    return results
}

/**
 * Search for Pixelfed users by handle or username.
 * Uses the Mastodon-compatible v2 search API.
 * @throws {Error} Network errors or API failures
 */
export async function searchPixelfedUsers(
    query: string,
    pixelfedSession: PixelfedSession
): Promise<SearchResult[]> {
    const res = await fetch(
        `${pixelfedSession.instance_url}/api/v2/search?q=${encodeURIComponent(query)}&type=accounts&limit=${SEARCH_RESULTS_LIMIT}`,
        {
            headers: {
                Authorization: `Bearer ${pixelfedSession.access_token}`,
            },
        }
    )

    if (!res.ok) {
        throw new Error(`Pixelfed search failed: ${res.status}`)
    }

    const data = await res.json()
    return data.accounts.map((account: MastodonAccount) => {
        const fullHandle = pixelfedNormalizeAcct(account.acct, pixelfedSession.instance_url)
        return {
            id: fullHandle,
            display: fullHandle,
            avatar: account.avatar,
            protocol: 'pixelfed' as const,
        }
    })
}

/**
 * Search for Pixelfed posts (statuses) by query string.
 * Uses the Mastodon-compatible v2 search API with type=statuses.
 * Returns a PostsPage with pixelfedCursor (next offset) for pagination.
 * @throws {Error} Network errors or API failures
 */
export async function searchPixelfedPosts(
    query: string,
    pixelfedSession: PixelfedSession,
    offset?: number
): Promise<PostsPage> {
    const params = new URLSearchParams({
        q: query,
        type: 'statuses',
        limit: String(POST_SEARCH_PAGE_SIZE),
    })
    if (offset !== undefined) params.set('offset', String(offset))

    const res = await fetch(`${pixelfedSession.instance_url}/api/v2/search?${params}`, {
        headers: {
            Authorization: `Bearer ${pixelfedSession.access_token}`,
        },
    })

    if (!res.ok) {
        throw new Error(`Pixelfed post search failed: ${res.status}`)
    }

    const data = await res.json()
    const posts = (data.statuses ?? []).map((status: MastodonStatus) =>
        mapPixelfedStatus(status, pixelfedSession.instance_url)
    )
    const currentOffset = offset ?? 0
    const nextOffset = posts.length === 0 ? undefined : currentOffset + posts.length

    return {
        posts,
        pixelfedCursor: nextOffset !== undefined ? String(nextOffset) : undefined,
    }
}
